(() => {
  if (customElements.get('sniffcar-scent-guide')) return;

  class SniffcarScentGuide extends HTMLElement {
    connectedCallback() {
      if (this.scentGuideReady) return;

      this.tabList = this.querySelector('[data-scent-tabs]');
      this.tabs = Array.from(this.querySelectorAll('[data-scent-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-scent-panel]'));
      this.filters = Array.from(this.querySelectorAll('[data-scent-filter]'));
      this.previousButton = this.querySelector('[data-scent-prev]');
      this.nextButton = this.querySelector('[data-scent-next]');
      this.counter = this.querySelector('[data-scent-count]');
      this.status = this.querySelector('[data-scent-status]');

      if (!this.tabList || !this.tabs.length || !this.panels.length) return;

      // This guide only changes its own presentation, never the product form.
      this.scentGuideReady = true;
      this.tabList.setAttribute('role', 'tablist');
      this.tabList.setAttribute('aria-orientation', 'horizontal');
      this.tabs.forEach(tab => tab.setAttribute('role', 'tab'));
      this.panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('tabindex', '0');
      });
      if (this.status) {
        this.status.setAttribute('role', 'status');
        this.status.setAttribute('aria-live', 'polite');
        this.status.setAttribute('aria-atomic', 'true');
      }

      this.family = 'all';
      const initialTab = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.activeScent = initialTab.dataset.scentTab;
      this.applyFilter('all', false);
      this.classList.add('is-ready');

      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;

        if (this.filters.includes(button)) {
          this.applyFilter(button.dataset.scentFilter);
        } else if (this.tabs.includes(button) && !button.hidden) {
          this.selectScent(button.dataset.scentTab);
        } else if (button === this.previousButton || button === this.nextButton) {
          const direction = button === this.previousButton ? -1 : 1;
          const index = this.visibleTabs.findIndex(tab => tab.dataset.scentTab === this.activeScent);
          const nextTab = this.visibleTabs[index + direction];
          if (nextTab) this.selectScent(nextTab.dataset.scentTab);
        }
      });

      this.addEventListener('shopify:block:select', event => {
        const tab = event.target.closest('[data-scent-tab]');
        if (!tab) return;
        this.applyFilter('all', false);
        this.selectScent(tab.dataset.scentTab);
      });

      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-scent-tab]');
        if (!this.visibleTabs.includes(tab)) return;

        const index = this.visibleTabs.indexOf(tab);
        const lastIndex = this.visibleTabs.length - 1;
        let nextIndex;

        if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
        else if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = lastIndex;
        else return;

        event.preventDefault();
        const nextTab = this.visibleTabs[nextIndex];
        nextTab.focus();
        this.selectScent(nextTab.dataset.scentTab);
      });
    }

    applyFilter(family, reveal = true) {
      const matchingTabs = this.tabs.filter(tab => family === 'all' || tab.dataset.scentFamily === family);
      if (!matchingTabs.length) return;

      this.family = family;
      this.visibleTabs = matchingTabs;
      this.filters.forEach(filter => {
        filter.setAttribute('aria-pressed', String(filter.dataset.scentFilter === family));
      });
      this.tabs.forEach(tab => { tab.hidden = !matchingTabs.includes(tab); });

      const activeTab = matchingTabs.find(tab => tab.dataset.scentTab === this.activeScent) || matchingTabs[0];
      this.selectScent(activeTab.dataset.scentTab, reveal);
    }

    selectScent(scent, reveal = true) {
      const activeTab = this.visibleTabs.find(tab => tab.dataset.scentTab === scent);
      if (!activeTab) return;

      this.activeScent = scent;
      this.tabs.forEach(tab => {
        const selected = tab === activeTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => {
        panel.hidden = panel.dataset.scentPanel !== scent;
      });

      const index = this.visibleTabs.indexOf(activeTab);
      if (this.previousButton) this.previousButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.visibleTabs.length - 1;
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.visibleTabs.length}`;
      if (this.status) {
        const panel = this.panels.find(item => item.dataset.scentPanel === scent);
        const name = panel?.querySelector('.sc-scent__name')?.textContent.trim() || activeTab.textContent.trim();
        this.status.textContent = name;
      }
      if (reveal) this.revealTab(activeTab);
    }

    revealTab(tab) {
      const listBounds = this.tabList.getBoundingClientRect();
      const tabBounds = tab.getBoundingClientRect();
      const leftEdge = listBounds.left + this.tabList.clientLeft;
      const rightEdge = leftEdge + this.tabList.clientWidth;
      let nextLeft = this.tabList.scrollLeft;

      if (tabBounds.left < leftEdge) nextLeft += tabBounds.left - leftEdge;
      else if (tabBounds.right > rightEdge) nextLeft += tabBounds.right - rightEdge;
      else return;

      // Scroll the scent strip only; selecting a scent must not move the page.
      const maximum = Math.max(0, this.tabList.scrollWidth - this.tabList.clientWidth);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.tabList.scrollTo({
        left: Math.max(0, Math.min(nextLeft, maximum)),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }
  }

  customElements.define('sniffcar-scent-guide', SniffcarScentGuide);
})();
